import * as FileSaver from "file-saver";
import * as XLSX from "xlsx";
import { groupBy } from "lodash";
import moment from "moment-timezone";
import dayjsTZ from "../../../../utils/dayjs";
import { buildTimecardSummary, SUMMARY_COLS } from "./timecardSummary";

// excel sheet names must be unique, non-empty, <= 31 chars and free of : \ / ? * [ ].
// violating any of these produces a workbook Excel can only "recover" into one sheet.
const makeSafeSheetNamer = () => {
  const used = new Set();
  return (name) => {
    let clean =
      String(name ?? "")
        .replace(/[:\\/?*[\]]/g, " ")
        .replace(/\s+/g, " ")
        .trim()
        .slice(0, 31) || "Sheet";
    let candidate = clean;
    let i = 1;
    while (used.has(candidate)) {
      const suffix = `_${i++}`;
      candidate = clean.slice(0, 31 - suffix.length) + suffix;
    }
    used.add(candidate);
    return candidate;
  };
};

// format a picker value directly (preserving the calendar day the user picked).
// re-wrapping through dayjsTZ would re-convert the instant under a per-date DST
// offset and shift the day across midnight (e.g. 1 March -> 28/29 February).
const formatDay = (v) =>
  v && typeof v.format === "function"
    ? v.format("DD/MM/YYYY")
    : v
      ? dayjsTZ(v).format("DD/MM/YYYY")
      : "";

const FILE_TYPE =
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";

const coerceNumberCells = (rows, columns) =>
  rows.map((row) => {
    const out = { ...row };
    columns.forEach((col) => {
      const v = out[col];
      if (typeof v === "number") {
        out[col] = parseFloat(Number(v).toFixed(2));
      } else if (
        typeof v === "string" &&
        v.trim() !== "" &&
        /^-?\d+(\.\d+)?$/.test(v.trim())
      ) {
        out[col] = parseFloat(Number(v).toFixed(2));
      }
    });
    return out;
  });

const appendTotalsRow = (rows, columns, numericColumns) => {
  const totalsRow = {};
  numericColumns.forEach((col) => {
    totalsRow[col] = parseFloat(
      rows
        .reduce(
          (sum, r) =>
            sum + (typeof r[col] === "number" ? r[col] : Number(r[col]) || 0),
          0,
        )
        .toFixed(2),
    );
  });
  if (columns[0] && !numericColumns.includes(columns[0])) {
    totalsRow[columns[0]] = "TOTALI";
  }
  return [...rows, totalsRow];
};

const addSheet = (wb, safeSheetName, name, rows, columns) => {
  const sheetName = safeSheetName(name);
  wb.SheetNames.push(sheetName);
  wb.Sheets[sheetName] = XLSX.utils.json_to_sheet(rows, { header: columns });
  wb.Sheets[sheetName]["!cols"] = columns.map((col) => ({
    wch: Math.min(
      28,
      Math.max(
        12,
        String(col).length + 2,
        ...rows.map((r) => String(r?.[col] ?? "").length + 1),
      ),
    ),
  }));
};

/**
 * Excel export: optional summary sheet first, then one sheet per employee.
 * @param {Array} apiData detail rows (Albanian headers)
 * @param {string} fileName
 * @param {*} time interval
 * @param {string} note
 * @param {{ includeSummary?: boolean, summaryRows?: Array }} [options]
 */
export const exportToCSV = (apiData, fileName, time, note, options = {}) => {
  if (!Array.isArray(apiData) || apiData.length === 0) return;
  let dateRange =
    time?.[0] && time?.[1]
      ? `${formatDay(time[0])} - ${formatDay(time[1])}`
      : "";
  const fileExtension = ".xlsx";
  const safeSheetName = makeSafeSheetNamer();
  // one sheet per employee. the combined-name column ("Punonjësi") isn't always
  // present — some report configs use separate first/last name columns — so fall
  // back to "Emri" + "Mbiemri" before lumping rows into a single group.
  const groupKeyForRow = (row) => {
    const full = row?.["Punonjësi"];
    if (full != null && String(full).trim() !== "") return String(full).trim();
    const first = String(row?.["Emri"] ?? "").trim();
    const last = String(row?.["Mbiemri"] ?? "").trim();
    return `${first} ${last}`.trim() || "Të tjera";
  };
  const grouped = groupBy(apiData, groupKeyForRow);

  // only the selected/exported columns — never inject extra ones.
  const columns = Object.keys(apiData[0]);
  const firstColumn = columns[0];
  // numeric columns are the only ones that get a summed total.
  const numericColumns = columns.filter((col) =>
    apiData.some(
      (row) =>
        typeof row[col] === "number" ||
        (typeof row[col] === "string" && /^-?\d+(\.\d+)?$/.test(String(row[col]).trim())),
    ),
  );

  let wbtest = { Sheets: {}, SheetNames: [] };

  const includeSummary = options.includeSummary !== false && !!apiData[0]?.["Punonjësi"];
  if (includeSummary) {
    const summaryRows =
      options.summaryRows || buildTimecardSummary(apiData);
    if (summaryRows.length) {
      const summaryCols = Object.values(SUMMARY_COLS);
      const summaryNumeric = summaryCols.filter((c) => c !== SUMMARY_COLS.employee && c !== SUMMARY_COLS.department && c !== SUMMARY_COLS.team);
      const withTotals = appendTotalsRow(
        coerceNumberCells(summaryRows, summaryCols),
        summaryCols,
        summaryNumeric,
      );
      if (note !== "" && !!note) {
        withTotals.push({});
        withTotals.push({ [SUMMARY_COLS.employee]: "Shenimi" });
        withTotals.push({ [SUMMARY_COLS.employee]: note.toString() });
      }
      addSheet(wbtest, safeSheetName, "Përmbledhje", withTotals, summaryCols);
    }
  }

  Object.entries(grouped)
    .sort(([a], [b]) => a.localeCompare(b, "sq", { sensitivity: "base" }))
    .forEach(([key, value]) => {
      const coerced = coerceNumberCells(value, columns);
      let rows = appendTotalsRow(coerced, columns, numericColumns);

      if (note !== "" && !!note) {
        rows.push({}); // blank spacer row
        rows.push({ [firstColumn]: "Shenimi" });
        rows.push({ [firstColumn]: note.toString() });
      }

      addSheet(wbtest, safeSheetName, key, rows, columns);
    });
  const excelBuffer = XLSX.write(wbtest, { bookType: "xlsx", type: "array" });
  const data = new Blob([excelBuffer], { type: FILE_TYPE });
  FileSaver.saveAs(data, fileName + dateRange + fileExtension);
};
